import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListGroup } from 'store/services/collectionList/types';

export type GroupKind = 'color' | 'set';

export interface GroupSection<T> {
  group: ListGroup;
  rows: T[];
  collapsed: boolean;
}

export interface GroupedRows<T> {
  /** null when the current sort is not a bucket sort or the response has no groups. */
  sections: GroupSection<T>[] | null;
  kind: GroupKind | null;
  toggle: (key: string) => void;
}

/** The sort keys that bucket the list (spec decision 7). */
export const groupKindForSort = (sortKey: string | undefined): GroupKind | null => {
  if (!sortKey) return null;
  const base = sortKey.replace(/-(asc|desc)$/, '');
  if (base === 'color_identity') return 'color';
  if (base === 'set_released') return 'set';
  return null;
};

interface StoredCollapse {
  sortKey: string;
  collapsed: string[];
}

const read = (storageKey: string): StoredCollapse | null => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as StoredCollapse) : null;
  } catch {
    return null;
  }
};

const write = (storageKey: string, value: StoredCollapse) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    /* storage unavailable: collapse state is per-render only */
  }
};

/**
 * Split a bucket-sorted page into sections in server order.
 *
 * Rows arrive already sorted by the backend and each carries `groupKey`; the
 * hook never classifies a card itself, it only folds rows under the header the
 * server put them in. Collapsed state is per surface and per sort key in
 * sessionStorage, and is cleared whenever the sort changes (a Color bucket you
 * folded has no meaning under the Set sort).
 */
export const useGroupedRows = <T extends { groupKey?: string }>(
  rows: T[],
  groups: ListGroup[] | undefined,
  sortKey: string | undefined,
  storageKey: string,
): GroupedRows<T> => {
  const kind = groupKindForSort(sortKey);
  const scope = `${storageKey}:groups`;

  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const stored = read(scope);
    return stored && stored.sortKey === sortKey ? new Set(stored.collapsed) : new Set();
  });

  // Sort change: forget every fold from the previous sort.
  useEffect(() => {
    const stored = read(scope);
    if (!stored || stored.sortKey !== sortKey) {
      setCollapsed(new Set());
      if (sortKey) write(scope, { sortKey, collapsed: [] });
    }
  }, [scope, sortKey]);

  const toggle = useCallback(
    (key: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        if (sortKey) write(scope, { sortKey, collapsed: Array.from(next) });
        return next;
      });
    },
    [scope, sortKey],
  );

  const sections = useMemo<GroupSection<T>[] | null>(() => {
    if (!kind || !groups || groups.length === 0) return null;
    const byKey = new Map<string, T[]>();
    for (const row of rows) {
      const key = row.groupKey ?? '';
      const bucket = byKey.get(key) ?? [];
      bucket.push(row);
      byKey.set(key, bucket);
    }
    const result: GroupSection<T>[] = [];
    for (const group of groups) {
      const bucketRows = byKey.get(group.key);
      // A bucket whose rows all live on another page gets no header on this one.
      if (!bucketRows || bucketRows.length === 0) continue;
      result.push({ group, rows: bucketRows, collapsed: collapsed.has(group.key) });
      byKey.delete(group.key);
    }
    // Rows the server did not key (should not happen) stay visible under a
    // neutral header rather than vanishing.
    const orphans = Array.from(byKey.values()).flat();
    if (orphans.length > 0) {
      result.push({
        group: {
          key: '_other',
          label: 'Other',
          cardCount: orphans.length,
          copyCount: orphans.reduce((sum, r) => sum + ((r as { quantity?: number }).quantity ?? 1), 0),
          pointTotal: 0,
        },
        rows: orphans,
        collapsed: collapsed.has('_other'),
      });
    }
    return result;
  }, [kind, groups, rows, collapsed]);

  return { sections, kind, toggle };
};

export default useGroupedRows;
